import { useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Loader2, Plus, Copy, Check, RefreshCw, Pencil, Trash2, KeyRound, Power, Users, Download } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PLAN_OPTIONS_FULL = [
  { value: "none", label: "Nessun piano" },
  { value: "assicurazioni", label: "Assicurazioni" },
  { value: "professional", label: "Professional" },
  { value: "enterprise", label: "Enterprise Multi-Sede" },
];

const PLAN_BADGES = {
  assicurazioni: { txt: "Assicurazioni", cls: "bg-amber-100 text-amber-800" },
  professional: { txt: "Professional", cls: "bg-blue-100 text-blue-800" },
  enterprise: { txt: "Enterprise", cls: "bg-violet-100 text-violet-800" },
};

function fmtDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

// === CREATE NEW CONSULTANT ===
function CreateDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "", first_name: "", last_name: "", phone: "",
    studio_name: "", slug: "", password: "",
    plan: "professional", billing_cycle: "annual", duration_months: 2,
    admin_notes: "Promo lancio 2 mesi gratis",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = { ...form };
      body.duration_months = parseInt(body.duration_months) || 1;
      if (!body.plan || body.plan === "none") delete body.plan;
      if (!body.password) delete body.password;
      if (!body.slug) delete body.slug;
      const { data } = await api.post("/admin/consultants/create", body);
      setResult(data);
      toast.success("Consulente creato");
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione");
    } finally { setLoading(false); }
  };

  const copyCreds = () => {
    if (!result) return;
    const txt = `GoStreet — Le tue credenziali di accesso

Studio: ${result.consultant.studio_name}
Email: ${result.user.email}
Password: ${result.plaintext_password}
Link accesso: ${result.login_url}
Slug pubblico: /c/${result.consultant.slug}
${result.subscription ? `\nPiano attivo: ${result.subscription.plan.toUpperCase()} (${result.subscription.status})\nScadenza: ${new Date(result.subscription.current_period_end).toLocaleDateString("it-IT")}` : ""}`;
    navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Credenziali copiate negli appunti");
  };

  const close = () => {
    setOpen(false);
    setResult(null);
    setForm({
      email: "", first_name: "", last_name: "", phone: "",
      studio_name: "", slug: "", password: "",
      plan: "professional", billing_cycle: "annual", duration_months: 2,
      admin_notes: "Promo lancio 2 mesi gratis",
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else setOpen(true); }}>
      <DialogTrigger asChild>
        <button data-testid="open-create-consultant-btn" className="inline-flex items-center gap-2 bg-[#2456C6] hover:bg-[#1B47AC] text-white text-sm font-bold px-4 py-2 rounded-xl">
          <Plus className="w-4 h-4" /> Crea consulente
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        {result ? (
          <>
            <DialogHeader><DialogTitle>Consulente creato ✓</DialogTitle></DialogHeader>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
              <strong>⚠️ Salva ora queste credenziali</strong>: la password in chiaro non sarà più visibile.
            </div>
            <div className="rounded-xl border border-slate-200 p-4 space-y-2 text-sm font-mono bg-slate-50">
              <div><span className="text-slate-500">Studio:</span> <strong>{result.consultant.studio_name}</strong></div>
              <div><span className="text-slate-500">Email:</span> <strong data-testid="created-email">{result.user.email}</strong></div>
              <div><span className="text-slate-500">Password:</span> <strong className="text-[#F77F2A]" data-testid="created-password">{result.plaintext_password}</strong></div>
              <div><span className="text-slate-500">Link:</span> {result.login_url}</div>
              <div><span className="text-slate-500">Slug:</span> /c/{result.consultant.slug}</div>
              {result.subscription && (
                <div className="pt-2 border-t border-slate-200 mt-2">
                  <span className="text-slate-500">Piano:</span> <strong>{result.subscription.plan.toUpperCase()}</strong> ({result.subscription.status}) ·
                  scadenza {new Date(result.subscription.current_period_end).toLocaleDateString("it-IT")}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close} data-testid="created-close-btn">Chiudi</Button>
              <Button onClick={copyCreds} data-testid="copy-creds-btn">
                {copied ? <><Check className="w-4 h-4 mr-1" /> Copiate</> : <><Copy className="w-4 h-4 mr-1" /> Copia credenziali</>}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader><DialogTitle>Crea consulente manualmente</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Nome *</Label><Input required value={form.first_name} onChange={(e) => update("first_name", e.target.value)} data-testid="create-firstname" /></div>
                <div><Label>Cognome *</Label><Input required value={form.last_name} onChange={(e) => update("last_name", e.target.value)} data-testid="create-lastname" /></div>
              </div>
              <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="create-email" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => update("phone", e.target.value)} /></div>
                <div><Label>Password (lascia vuoto = auto)</Label><Input value={form.password} onChange={(e) => update("password", e.target.value)} placeholder="auto-generata" data-testid="create-password" /></div>
              </div>
              <div><Label>Nome studio *</Label><Input required value={form.studio_name} onChange={(e) => update("studio_name", e.target.value)} data-testid="create-studio" /></div>
              <div><Label>Slug pubblico (lascia vuoto = auto da nome studio)</Label><Input value={form.slug} onChange={(e) => update("slug", e.target.value)} placeholder="es. rossi-infortunistica" /></div>

              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-bold text-[#1E293B] mb-2">Piano regalo (opzionale)</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Piano</Label>
                    <Select value={form.plan} onValueChange={(v) => update("plan", v)}>
                      <SelectTrigger data-testid="create-plan-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PLAN_OPTIONS_FULL.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Durata (mesi)</Label>
                    <Input type="number" min="1" max="60" value={form.duration_months} onChange={(e) => update("duration_months", e.target.value)} data-testid="create-duration" />
                  </div>
                </div>
                <div className="mt-2">
                  <Label>Note interne</Label>
                  <Textarea value={form.admin_notes} onChange={(e) => update("admin_notes", e.target.value)} rows={2} />
                </div>
              </div>

              <DialogFooter>
                <Button type="submit" disabled={loading} data-testid="create-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea consulente"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// === EDIT EXISTING CONSULTANT ===
function EditDialog({ consultant, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => ({
    email: consultant.email || "",
    first_name: consultant.first_name || "",
    last_name: consultant.last_name || "",
    phone: consultant.phone || "",
    studio_name: consultant.studio_name || "",
    slug: consultant.slug || "",
    notification_email: consultant.notification_email || "",
    password: "",
    feature_otp: consultant.feature_otp !== false,
    feature_signature_confirm: consultant.feature_signature_confirm !== false,
    feature_status_check: consultant.feature_status_check !== false,
    feature_photo: consultant.feature_photo !== false,
    feature_video: consultant.feature_video !== false,
    feature_audio: consultant.feature_audio !== false,
    feature_description: consultant.feature_description !== false,
  }));
  const [loading, setLoading] = useState(false);
  const [newPwd, setNewPwd] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        email: consultant.email || "",
        first_name: consultant.first_name || "",
        last_name: consultant.last_name || "",
        phone: consultant.phone || "",
        studio_name: consultant.studio_name || "",
        slug: consultant.slug || "",
        notification_email: consultant.notification_email || "",
        password: "",
        feature_otp: consultant.feature_otp !== false,
        feature_signature_confirm: consultant.feature_signature_confirm !== false,
        feature_status_check: consultant.feature_status_check !== false,
        feature_photo: consultant.feature_photo !== false,
        feature_video: consultant.feature_video !== false,
        feature_audio: consultant.feature_audio !== false,
        feature_description: consultant.feature_description !== false,
      });
      setNewPwd(null);
    }
  }, [open, consultant]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = { ...form };
      if (!body.password) delete body.password;
      const { data } = await api.patch(`/admin/consultants/${consultant.id}`, body);
      if (data.new_password) {
        setNewPwd(data.new_password);
        toast.success("Aggiornato. Nuova password generata.");
      } else {
        toast.success("Consulente aggiornato");
        setOpen(false);
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  };

  const resetPassword = async () => {
    if (!window.confirm("Generare nuova password? Quella attuale non funzionerà più.")) return;
    try {
      const { data } = await api.post(`/admin/consultants/${consultant.id}/reset-password`);
      setNewPwd(data.new_password);
      toast.success("Nuova password generata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const copyPwd = () => {
    navigator.clipboard.writeText(`Email: ${form.email}\nPassword: ${newPwd}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copiate");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`edit-consultant-${consultant.slug}`} className="p-2 hover:bg-slate-100 rounded-lg" title="Modifica">
          <Pencil className="w-4 h-4 text-slate-600" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifica {consultant.studio_name}</DialogTitle>
          <DialogDescription>Modifica i dati di accesso e dello studio</DialogDescription>
        </DialogHeader>

        {newPwd && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm">
            <div className="font-bold text-amber-900 mb-1">⚠️ Nuova password generata</div>
            <div className="font-mono text-base text-[#F77F2A] font-black" data-testid="edit-new-password">{newPwd}</div>
            <Button size="sm" onClick={copyPwd} className="mt-2 h-7 text-xs" data-testid="copy-new-pwd-btn">
              {copied ? <><Check className="w-3 h-3 mr-1" /> Copiate</> : <><Copy className="w-3 h-3 mr-1" /> Copia email + password</>}
            </Button>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Nome</Label><Input value={form.first_name} onChange={(e) => setForm((s) => ({ ...s, first_name: e.target.value }))} data-testid="edit-firstname" /></div>
            <div><Label>Cognome</Label><Input value={form.last_name} onChange={(e) => setForm((s) => ({ ...s, last_name: e.target.value }))} data-testid="edit-lastname" /></div>
          </div>
          <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} data-testid="edit-email" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} /></div>
            <div>
              <Label>Imposta password</Label>
              <div className="flex gap-1">
                <Input value={form.password} onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))} placeholder="lascia vuoto" data-testid="edit-password" />
                <Button type="button" variant="outline" size="icon" onClick={resetPassword} title="Genera nuova" data-testid="reset-pwd-btn">
                  <KeyRound className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <div><Label>Nome studio</Label><Input value={form.studio_name} onChange={(e) => setForm((s) => ({ ...s, studio_name: e.target.value }))} data-testid="edit-studio" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Slug (link pubblico)</Label><Input value={form.slug} onChange={(e) => setForm((s) => ({ ...s, slug: e.target.value }))} data-testid="edit-slug" /></div>
            <div><Label>Email notifiche</Label><Input type="email" value={form.notification_email} onChange={(e) => setForm((s) => ({ ...s, notification_email: e.target.value }))} /></div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
            <div className="text-xs font-black text-[#1E293B] mb-1">⚙️ Funzionalità del consulente</div>
            <p className="text-[11px] text-slate-500 -mt-1 mb-2">
              Disabilita queste per un flusso veloce di acquisizione (il consulente gestisce il resto in agenzia).
            </p>

            <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-white">
              <input type="checkbox" checked={form.feature_otp}
                onChange={(e) => setForm((s) => ({ ...s, feature_otp: e.target.checked }))}
                className="mt-1 w-4 h-4 accent-[#F77F2A]" data-testid="edit-feature-otp" />
              <span className="text-sm">
                <span className="font-bold text-[#1E293B]">OTP via email</span>
                <span className="block text-[11px] text-slate-500">Invio codice OTP al cliente alla creazione pratica. Se OFF, la pratica viene salvata in modalità rapida senza firma elettronica.</span>
              </span>
            </label>

            <label className={`flex items-start gap-3 p-2 rounded-lg hover:bg-white ${!form.feature_otp ? "opacity-40" : "cursor-pointer"}`}>
              <input type="checkbox" checked={form.feature_signature_confirm}
                disabled={!form.feature_otp}
                onChange={(e) => setForm((s) => ({ ...s, feature_signature_confirm: e.target.checked }))}
                className="mt-1 w-4 h-4 accent-[#F77F2A]" data-testid="edit-feature-signature" />
              <span className="text-sm">
                <span className="font-bold text-[#1E293B]">Conferma firma (link email)</span>
                <span className="block text-[11px] text-slate-500">Doppia conferma eIDAS: dopo OTP, il cliente riceve un link da cliccare. Se OFF, la firma è completa con il solo OTP.</span>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-white">
              <input type="checkbox" checked={form.feature_status_check}
                onChange={(e) => setForm((s) => ({ ...s, feature_status_check: e.target.checked }))}
                className="mt-1 w-4 h-4 accent-[#F77F2A]" data-testid="edit-feature-status" />
              <span className="text-sm">
                <span className="font-bold text-[#1E293B]">Stato pratica pubblico</span>
                <span className="block text-[11px] text-slate-500">Mostra il riquadro &quot;Stato pratica&quot; sulla pagina pubblica del consulente. Se OFF, i clienti non possono consultare le pratiche.</span>
              </span>
            </label>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
            <div className="text-xs font-black text-[#1E293B] mb-1">📋 Contenuti raccolti dal cliente</div>
            <p className="text-[11px] text-slate-500 -mt-1 mb-2">
              Disabilita ciò che non serve al consulente per un&apos;acquisizione lampo (nome + targa). Il consulente raccoglierà il resto in agenzia.
            </p>

            <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-white">
              <input type="checkbox" checked={form.feature_photo}
                onChange={(e) => setForm((s) => ({ ...s, feature_photo: e.target.checked }))}
                className="mt-1 w-4 h-4 accent-[#F77F2A]" data-testid="edit-feature-photo" />
              <span className="text-sm">
                <span className="font-bold text-[#1E293B]">📷 Foto del sinistro</span>
                <span className="block text-[11px] text-slate-500">Caricamento foto dal cellulare (disponibile dal piano Starter)</span>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-white">
              <input type="checkbox" checked={form.feature_video}
                onChange={(e) => setForm((s) => ({ ...s, feature_video: e.target.checked }))}
                className="mt-1 w-4 h-4 accent-[#F77F2A]" data-testid="edit-feature-video" />
              <span className="text-sm">
                <span className="font-bold text-[#1E293B]">🎬 Video del sinistro</span>
                <span className="block text-[11px] text-slate-500">Registrazione/upload video (disponibile dal piano Studio)</span>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-white">
              <input type="checkbox" checked={form.feature_audio}
                onChange={(e) => setForm((s) => ({ ...s, feature_audio: e.target.checked }))}
                className="mt-1 w-4 h-4 accent-[#F77F2A]" data-testid="edit-feature-audio" />
              <span className="text-sm">
                <span className="font-bold text-[#1E293B]">🎤 Audio del cliente</span>
                <span className="block text-[11px] text-slate-500">Registrazione vocale (disponibile dal piano Studio)</span>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-white">
              <input type="checkbox" checked={form.feature_description}
                onChange={(e) => setForm((s) => ({ ...s, feature_description: e.target.checked }))}
                className="mt-1 w-4 h-4 accent-[#F77F2A]" data-testid="edit-feature-description" />
              <span className="text-sm">
                <span className="font-bold text-[#1E293B]">📝 Descrizione testuale</span>
                <span className="block text-[11px] text-slate-500">Campo di testo libero in cui il cliente racconta come è avvenuto il sinistro</span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Chiudi</Button>
            <Button type="submit" disabled={loading} data-testid="edit-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// === INLINE PLAN SELECTOR ===
function InlinePlanSelector({ consultant, onChanged }) {
  const [loading, setLoading] = useState(false);
  const currentPlan = consultant.active_plan || "none";

  const handleChange = async (newPlan) => {
    if (newPlan === currentPlan) return;
    if (newPlan === "none") {
      // Cancel current subscription
      if (consultant.active_plan_id) {
        if (!window.confirm("Rimuovere il piano attuale? Il consulente non potrà creare nuove pratiche.")) return;
        setLoading(true);
        try {
          await api.delete(`/admin/subscriptions/${consultant.active_plan_id}`);
          toast.success("Piano rimosso");
          onChanged?.();
        } catch (err) { toast.error("Errore"); }
        setLoading(false);
      }
      return;
    }
    // Assign new gift plan with default 2 months
    const months = parseInt(window.prompt(`Quanti mesi per il piano ${newPlan.toUpperCase()}?`, "2"), 10);
    if (!months || months < 1) return;
    setLoading(true);
    try {
      await api.post("/admin/subscriptions", {
        consultant_id: consultant.id,
        plan: newPlan,
        billing_cycle: "annual",
        duration_months: months,
        status: "admin_gift",
        admin_notes: `Cambio piano da ${currentPlan} → ${newPlan} (${months} mesi)`,
      });
      toast.success(`Piano ${newPlan.toUpperCase()} attivato per ${months} mesi`);
      onChanged?.();
    } catch (err) { toast.error("Errore"); }
    setLoading(false);
  };

  return (
    <Select value={currentPlan} onValueChange={handleChange} disabled={loading}>
      <SelectTrigger data-testid={`plan-select-${consultant.slug}`} className="h-8 text-xs w-[140px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PLAN_OPTIONS_FULL.map((p) => (
          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// === CLIENTS LIST + CSV EXPORT ===
function ClientsDialog({ consultant }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/consultants/${consultant.id}/clients`);
      setData(data);
    } catch (e) {
      toast.error("Errore caricamento clienti");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("gs_token");
      const res = await fetch(`${BACKEND_URL}/api/admin/consultants/${consultant.id}/clients.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Errore download");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clienti_${consultant.slug}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV scaricato");
    } catch (e) {
      toast.error("Errore download CSV");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`view-clients-${consultant.slug}`} className="p-2 hover:bg-blue-50 rounded-lg" title="Vedi clienti registrati">
          <Users className="w-4 h-4 text-[#2456C6]" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#2456C6]" />
            Clienti registrati · {consultant.studio_name}
          </DialogTitle>
          <DialogDescription>
            Database clienti registrati tramite il QR / link pubblico di questo consulente
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#2456C6] mx-auto" /></div>
        ) : !data ? null : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="text-sm">
                <span className="text-slate-500">Totale clienti: </span>
                <strong data-testid="clients-total" className="text-[#1E293B]">{data.total}</strong>
                <span className="ml-3 text-slate-500">· Consensi marketing TotalKlick: </span>
                <strong className="text-[#F77F2A]" data-testid="clients-tk-consent-count">
                  {data.clients.filter((c) => c.marketing_totalklick_consent).length}
                </strong>
              </div>
              <Button onClick={downloadCsv} data-testid="download-clients-csv-btn" disabled={data.total === 0}>
                <Download className="w-4 h-4 mr-1" /> Scarica CSV
              </Button>
            </div>

            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto max-h-[60vh]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                      <th className="px-3 py-2">Cliente</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Telefono</th>
                      <th className="px-3 py-2">CF</th>
                      <th className="px-3 py-2">Indirizzo</th>
                      <th className="px-3 py-2">Targa</th>
                      <th className="px-3 py-2 text-center">Pratiche</th>
                      <th className="px-3 py-2 text-center">TotalKlick</th>
                      <th className="px-3 py-2">Registrato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.clients.length === 0 ? (
                      <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                        Nessun cliente registrato ancora. I clienti compaiono qui dopo essersi registrati tramite il QR / link pubblico del consulente.
                      </td></tr>
                    ) : data.clients.map((c) => (
                      <tr key={c.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-bold">{c.first_name} {c.last_name}</td>
                        <td className="px-3 py-2 text-xs">{c.email}</td>
                        <td className="px-3 py-2 text-xs">{c.phone}</td>
                        <td className="px-3 py-2 text-xs font-mono">{c.fiscal_code}</td>
                        <td className="px-3 py-2 text-xs">
                          {c.address_street && (<><span>{c.address_street}</span><br/></>)}
                          {[c.address_postal_code, c.address_city, c.address_province].filter(Boolean).join(" ")}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono">{c.vehicle_plate}</td>
                        <td className="px-3 py-2 text-center font-bold">{c.practices_count}</td>
                        <td className="px-3 py-2 text-center">
                          {c.marketing_totalklick_consent ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">SI</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500">NO</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {new Date(c.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// === MAIN PAGE ===
export default function AdminConsultants() {
  const [list, setList] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/consultants");
      setList(data);
    } catch { setList([]); }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/consultants/${c.id}`, { active: !c.active });
      load();
      toast.success(c.active ? "Disattivato" : "Riattivato");
    } catch { toast.error("Errore"); }
  };

  const softDelete = async (c) => {
    if (!window.confirm(`Disattivare definitivamente ${c.studio_name}? I dati delle pratiche saranno conservati per audit (GDPR).`)) return;
    try {
      await api.delete(`/admin/consultants/${c.id}`);
      toast.success("Consulente disattivato");
      load();
    } catch { toast.error("Errore"); }
  };

  const backfillClients = async () => {
    if (!window.confirm("Collegare i clienti storici (registrati prima dell'introduzione del tracking) ai consulenti in base alle pratiche già aperte? L'operazione è sicura e non duplica nulla.")) return;
    try {
      const { data } = await api.post("/admin/clients/backfill-consultant-links");
      toast.success(`Aggiornati ${data.updated} clienti storici (su ${data.total_scanned} scansionati)`);
      load();
    } catch (err) {
      toast.error("Errore durante il backfill");
    }
  };

  return (
    <DashboardLayout role="superadmin">
      <div className="p-4 sm:p-8">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-[#1E293B] mb-1">Consulenti</h1>
            <p className="text-sm text-slate-500">Crea, modifica e gestisci i consulenti</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={backfillClients} data-testid="backfill-clients-btn"
              className="inline-flex items-center gap-1.5 bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-bold px-3 py-2 rounded-lg" title="Collega clienti storici ai consulenti">
              <Users className="w-3.5 h-3.5" /> Recupera clienti storici
            </button>
            <button onClick={load} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#2456C6] px-3 py-2" data-testid="refresh-consultants-btn">
              <RefreshCw className="w-4 h-4" /> Aggiorna
            </button>
            <CreateDialog onCreated={load} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">
                  <th className="px-4 py-3">Studio</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Slug</th>
                  <th className="px-4 py-3">Piano</th>
                  <th className="px-4 py-3">Scadenza</th>
                  <th className="px-4 py-3">Pratiche</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {list === null ? (
                  <tr><td colSpan={8} className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#2456C6] mx-auto" /></td></tr>
                ) : list.length === 0 ? (
                  <tr><td colSpan={8} className="p-12 text-center text-slate-500">Nessun consulente registrato</td></tr>
                ) : (
                  list.map((c) => {
                    const planBadge = c.active_plan ? PLAN_BADGES[c.active_plan] : null;
                    return (
                      <tr key={c.id} className={`border-t border-slate-100 hover:bg-slate-50 ${!c.active ? "opacity-50" : ""}`} data-testid={`consultant-row-${c.slug}`}>
                        <td className="px-4 py-3 font-bold text-[#1E293B]">{c.studio_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{c.email}</td>
                        <td className="px-4 py-3 text-sm font-mono text-[#2456C6]">/c/{c.slug}</td>
                        <td className="px-4 py-3">
                          <InlinePlanSelector consultant={c} onChanged={load} />
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(c.active_plan_end)}</td>
                        <td className="px-4 py-3 text-sm font-bold text-center">{c.practices_count}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => toggleActive(c)} data-testid={`toggle-active-${c.slug}`}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                              c.active ? "bg-green-100 text-green-800 hover:bg-green-200" : "bg-slate-200 text-slate-600 hover:bg-slate-300"
                            }`}>
                            <Power className="w-3 h-3" />
                            {c.active ? "Attivo" : "Disattivo"}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <ClientsDialog consultant={c} />
                            <EditDialog consultant={c} onSaved={load} />
                            <button onClick={() => softDelete(c)} data-testid={`delete-consultant-${c.slug}`}
                              className="p-2 hover:bg-rose-50 rounded-lg" title="Elimina (soft delete)">
                              <Trash2 className="w-4 h-4 text-rose-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-slate-400 mt-4">
          💡 L&apos;eliminazione è una <strong>disattivazione</strong>: i dati delle pratiche, le firme e i media sono conservati per audit e GDPR.
          Per cancellazione permanente contatta il supporto.
        </p>
      </div>
    </DashboardLayout>
  );
}
